import {
  doc,
  getDoc,
  getDocs,
  collection,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { firebaseFirestore } from '@/infrastructure/firebase/firebaseFirestore.js';
import { DatasetStorageMode, type Dataset } from '@k-ucap/shared';
import { getCurrentEnvironment, buildConverter, toTimestampString } from './firestoreUtils.js';
import type { DatasetRepository } from '@/infrastructure/repositories/RepositoryInterfaces.js';

const DATASETS = 'datasets';

const converter = buildConverter<Dataset>(
  (data) =>
    ({
      ...data,
      createdAt: toTimestampString(data.createdAt) ?? new Date().toISOString(),
      updatedAt: toTimestampString(data.updatedAt) ?? new Date().toISOString(),
      lastUsedAt: toTimestampString(data.lastUsedAt) ?? undefined,
    }) as Dataset,
  (model) => ({ ...model, createdAt: serverTimestamp(), updatedAt: serverTimestamp() })
);

export class FirestoreDatasetRepository implements DatasetRepository {
  async findAll(): Promise<Dataset[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, DATASETS),
      where('environment', '==', getCurrentEnvironment()),
      orderBy('updatedAt', 'desc'),
      limit(200)
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findById(id: string): Promise<Dataset | undefined> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const snap = await getDoc(doc(db, DATASETS, id).withConverter(converter));
    return snap.data();
  }

  async findByProjectId(projectId: string): Promise<Dataset[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, DATASETS),
      where('projectId', '==', projectId),
      where('environment', '==', getCurrentEnvironment()),
      orderBy('updatedAt', 'desc'),
      limit(200)
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async save(dataset: Dataset): Promise<Dataset> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    if (dataset.storageMode === DatasetStorageMode.FIRESTORE) {
      throw new Error('FIRESTORE dataset storage mode is not yet fully supported in this phase. Use INLINE.');
    }
    if (dataset.storageMode === DatasetStorageMode.FILE) {
      throw new Error('FILE dataset storage mode requires Firebase Storage. Use INLINE.');
    }
    const data = { ...dataset, environment: getCurrentEnvironment() };
    const existing = await this.findById(dataset.id);
    await setDoc(doc(db, DATASETS, dataset.id).withConverter(converter), data);
    return { ...data, createdAt: existing?.createdAt ?? dataset.createdAt, updatedAt: new Date().toISOString() };
  }

  async delete(id: string): Promise<void> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    await deleteDoc(doc(db, DATASETS, id));
  }

  async duplicate(id: string, newName?: string): Promise<Dataset | undefined> {
    const original = await this.findById(id);
    if (!original) return undefined;
    const duplicated: Dataset = {
      ...original,
      id: `dataset-${Date.now()}`,
      name: newName ?? `${original.name} (Copy)`,
      parentId: original.id,
      revision: 1,
      updatedAt: new Date().toISOString(),
    };
    return this.save(duplicated);
  }
}

export const createFirestoreDatasetRepository = (): FirestoreDatasetRepository => new FirestoreDatasetRepository();
